/**
 * Analytics Route Tracker Component
 *
 * Tracks page views on route changes using React Router.
 * Also initialises Meta Pixel (gated by marketing consent).
 */
import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { initAnalytics, trackPageView } from "@/utils/analytics";
import {
  initMetaPixel,
  trackMetaPageView,
  hasMarketingConsent,
} from "@/utils/metaPixel";

const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || "";

export default function AnalyticsTracker() {
  const location = useLocation();
  const initialized = useRef(false);
  const previousPath = useRef(null);

  const tryInitPixel = useCallback(() => {
    if (META_PIXEL_ID && hasMarketingConsent()) {
      initMetaPixel(META_PIXEL_ID);
    }
  }, []);

  // Initialize analytics + pixel on mount
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      initAnalytics();
      tryInitPixel();
    }

    // Re-check consent when the user changes it
    const onConsent = () => tryInitPixel();
    window.addEventListener("consentChanged", onConsent);
    return () => window.removeEventListener("consentChanged", onConsent);
  }, [tryInitPixel]);

  // Track page views on route changes
  useEffect(() => {
    if (previousPath.current === null) {
      previousPath.current = location.pathname;
      return;
    }

    if (previousPath.current !== location.pathname) {
      previousPath.current = location.pathname;
      trackPageView();
      trackMetaPageView();
    }
  }, [location.pathname]);

  return null;
}
